import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
// import ChatBubbleOutlineOutlined from "@mui/icons-material/ChatBubbleOutlineOutlined";
import LockIcon from "@mui/icons-material/Lock";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneOutlined from "@mui/icons-material/NotificationsNoneOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { hasSubscriptionFeature } from "../../config/subscriptionConfig";
import { getMenuItemsForUser } from "../sidebar/Sidebar";
import "./mobileBottomBar.scss";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import MessageIcon from "@mui/icons-material/Message";
import PowerSettingsNew from "@mui/icons-material/PowerSettingsNew";
import { logoutUser } from "../../services/authService";

const setupItems = [
  { label: "Sessions", path: "/sessions" },
  { label: "Classes", path: "/classes" },
  { label: "Subjects", path: "/subjects" },
  { label: "Exams", path: "/exams" },
  {
    label: "Fee Books",
    path: "/fee-books",
    requiredFeature: "allowFeeRecords",
  },
  { label: "Import Staffs", path: "/import-staffs" },
  { label: "Import Students", path: "/import-students" },
];

const MobileBottomBar = () => {
  const location = useLocation();
  const { currentUser } = useSelector((state) => state.user);
  const { currentSchool } = useSelector((state) => state.school);
  const [sheetOpen, setSheetOpen] = useState(false);

  const dispatch = useDispatch();

  const menuItems = useMemo(
    () => getMenuItemsForUser(currentUser),
    [currentUser],
  );

  const primaryItems = menuItems.filter((item) => item.path).slice(0, 4);
  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isLocked = (item) =>
    item.requiredFeature &&
    !hasSubscriptionFeature(
      currentSchool?.subscription?.featureTier,
      item.requiredFeature,
    );

  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  const renderLink = (item, compact = false) => {
    const locked = isLocked(item);
    const content = (
      <>
        {item.icon || <PlayArrowIcon className="mobileBottomIcon" />}
        <span>{item.label}</span>
        {locked && <LockIcon className="mobileLockIcon" />}
      </>
    );

    if (locked) {
      return (
        <div
          className={`${compact ? "mobilePrimaryItem" : "mobileSheetLink"} locked`}
          key={item.label}
        >
          {content}
        </div>
      );
    }

    return (
      <Link
        className={`${compact ? "mobilePrimaryItem" : "mobileSheetLink"} ${isActive(item.path) ? "active" : ""}`}
        key={item.label}
        to={item.path}
      >
        {content}
      </Link>
    );
  };

  return (
    <>
      {sheetOpen && (
        <button
          className="mobileSheetBackdrop"
          aria-label="Close menu"
          onClick={() => setSheetOpen(false)}
        />
      )}
      <section
        className={`mobileActionSheet ${sheetOpen ? "open" : ""}`}
        aria-label="More navigation"
      >
        <div className="mobileSheetHeader">
          <div>
            <span className="mobileSheetEyebrow">Workspace</span>
            <h2>More</h2>
          </div>
          {currentSchool && (
            <span
              className={`subscriptionStatus ${currentSchool?.subscription?.status?.toLowerCase()}`}
            >
              {currentSchool?.subscription?.status === "Active"
                ? currentSchool?.subscription?.featureTier
                : currentSchool?.subscription?.status}
            </span>
          )}
          <button
            className="mobileSheetClose"
            aria-label="Close menu"
            onClick={() => setSheetOpen(false)}
          >
            <CloseIcon />
          </button>
        </div>
        <div className="mobileSheetContent">
          {/* <div style={{borderTop: "none"}} className="mobileSheetGroup mobileNavbarLinks">
            <div className="mobileSheetGroupTitle">
              <MenuIcon />
              <span>Quick access</span>
            </div>
            <Link
              className={`mobileSheetLink ${isActive("/notifications") ? "active" : ""}`}
              to="/notifications"
            >
              <NotificationsNoneOutlined />
              <span>Notifications</span>
            </Link>
            <Link
              className={`mobileSheetLink ${isActive("/messages") ? "active" : ""}`}
              to="/messages"
            >
              <ChatBubbleOutlineOutlined />
              <span>Messages</span>
            </Link>
          </div> */}
          <div className="mobileSheetGrid">
            {menuItems
              .filter((item) => !primaryItems.includes(item))
              .map((item) =>
                item.path ? (
                  renderLink(item)
                ) : (
                  <div className="mobileSheetGroup" key={item.label}>
                    <div className="mobileSheetGroupTitle">
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                    {item.children?.map((child) => renderLink(child))}
                  </div>
                ),
              )}
          </div>
          {currentUser?.role === "admin" && (
            <div className="mobileSheetGroup setupGroup">
              <div className="mobileSheetGroupTitle">
                <SettingsOutlinedIcon />
                <span>Setup</span>
              </div>
              {setupItems.map((item) => renderLink(item))}
            </div>
          )}

          <div className="mobileSheetTools">
            <Link to="/notifications">
              <NotificationsNoneOutlined />
              <span>Notifications</span>
            </Link>
            <Link to="/messages">
              <MessageIcon />
              <span>Messages</span>
            </Link>
            <Link to="/settings">
              <SettingsIcon />
              <span>Settings</span>
            </Link>
            <Link color="error" onClick={() => logoutUser(dispatch)}>
              <PowerSettingsNew />
              <span>Logout</span>
            </Link>
          </div>
        </div>
      </section>
      <nav className="mobileBottomBar" aria-label="Primary navigation">
        <div className="mobileBottomItems">
          {primaryItems.map((item) => renderLink(item, true))}
          <button
            className={`mobilePrimaryItem ${sheetOpen ? "active" : ""}`}
            onClick={() => setSheetOpen(true)}
          >
            <MenuIcon />
            <span>More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomBar;
